/**
 * The FAQs for `/tools/mastodon-limits`, in their own module.
 *
 * They live apart from the tool component on purpose. A route's `head` is
 * never code-split (only `component` and `loader` are), so importing this list
 * from the component module would pull the whole component — and its prose —
 * into the shared entry chunk that every visitor to every page downloads.
 * Measured: doing exactly that put this page's copy in `index-*.js` alongside
 * the router, while the sibling Bluesky tool stayed in its own route chunk.
 *
 * Rendered visibly on the page AND emitted as `FAQPage` JSON-LD from the route.
 * Marking up a question that is not on the page is a structured-data
 * violation, so both must read from this one list.
 */
export const MASTODON_LIMIT_FAQS = [
  {
    q: "What is the Mastodon character limit?",
    a: "It depends on the server you post from. Mastodon ships with a 500-character default, but each server can raise it and many do — hachyderm.io allows 2,263 characters and infosec.exchange allows 11,000. Enter your server above to see its real limit.",
  },
  {
    q: "Why does my server allow more than 500 characters?",
    a: "The limit is a server setting, not a property of Mastodon itself. Anyone running a server can raise it, and administrators often do to suit the community they host. Servers running other fediverse software, such as GoToSocial, ship with different defaults again.",
  },
  {
    q: "Do links count against the Mastodon character limit?",
    a: "A link costs a fixed number of characters however long the URL is — 23 on Mastodon, and 25 on GoToSocial. Servers publish this figure themselves, so this page reads it from yours rather than assuming. The server part of a remote mention like @you@example.social is free; only @you is counted.",
  },
  {
    q: "How long can Mastodon alt text be?",
    a: "Mastodon's default is 10,000 characters per image description, but like the post limit it is a per-server setting — GoToSocial defaults to 5,000. Your server's real figure appears above once you check it.",
  },
  {
    q: "How many images can one Mastodon post have?",
    a: "Four on a default Mastodon server. Other fediverse software differs — GoToSocial allows six. The number your server reports is shown above.",
  },
] as const;
